import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { invoicesAPI } from '@/lib/api';
import { formatCurrency, formatDate, getPaymentStatusColor } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { Plus, Search, FileText, Eye, Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

const ITEMS_PER_PAGE = 10;

export default function InvoicesPage() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [paymentStatus, setPaymentStatus] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const fetchInvoices = async () => {
    try {
      const params = {};
      if (search) params.search = search;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (paymentStatus && paymentStatus !== 'all') params.payment_status = paymentStatus;
      
      const res = await invoicesAPI.getAll(params);
      setInvoices(res.data);
      setCurrentPage(1);
    } catch (error) {
      toast.error('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, dateFrom, dateTo, paymentStatus]);

  const totalRevenue = invoices.reduce((sum, inv) => sum + (inv.grand_total || 0), 0);
  const totalProfit = invoices.reduce((sum, inv) => sum + (inv.profit || 0), 0);

  return (
    <div className="space-y-6 animate-fade-in" data-testid="invoices-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black font-heading tracking-tight">Invoices</h1>
          <p className="text-muted-foreground mt-1">View and manage all invoices</p>
        </div>
        <Link to="/invoices/new">
          <Button className="font-semibold uppercase tracking-wide" data-testid="new-invoice-btn">
            <Plus className="w-4 h-4 mr-2" />
            New Invoice
          </Button>
        </Link>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="md:col-span-2 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search invoice #, customer, vehicle..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="invoice-search-input"
              />
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-muted-foreground" />
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="flex-1"
                data-testid="date-from-input"
              />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">to</span>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="flex-1"
                data-testid="date-to-input"
              />
            </div>
            <Select value={paymentStatus} onValueChange={setPaymentStatus}>
              <SelectTrigger data-testid="status-filter">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="partial">Partial</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="stat-card">
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">Total Invoices</p>
            <p className="text-2xl font-bold font-heading">{invoices.length}</p>
          </CardContent>
        </Card>
        <Card className="stat-card">
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">Total Revenue</p>
            <p className="text-2xl font-bold font-heading">{formatCurrency(totalRevenue)}</p>
          </CardContent>
        </Card>
        <Card className="stat-card accent-green">
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">Total Profit</p>
            <p className="text-2xl font-bold font-heading text-green-600">{formatCurrency(totalProfit)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Invoices Table */}
      <Card data-testid="invoices-table-card">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="spinner" />
            </div>
          ) : invoices.length > 0 ? (
            <>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Invoice #</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Vehicle</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead className="text-right">Profit</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-20">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invoices.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE).map((invoice) => (
                    <TableRow key={invoice.id} data-testid={`invoice-row-${invoice.id}`}>
                      <TableCell className="font-mono font-medium">
                        {invoice.invoice_no}
                      </TableCell>
                      <TableCell>{formatDate(invoice.date)}</TableCell>
                      <TableCell>{invoice.customer_name}</TableCell>
                      <TableCell className="font-mono">{invoice.vehicle_number}</TableCell>
                      <TableCell className="text-right font-mono font-medium">
                        {formatCurrency(invoice.grand_total)}
                      </TableCell>
                      <TableCell className="text-right font-mono text-green-600">
                        {formatCurrency(invoice.profit)}
                      </TableCell>
                      <TableCell>
                        <Badge className={getPaymentStatusColor(invoice.payment_status)}>
                          {invoice.payment_status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Link to={`/invoices/${invoice.id}`}>
                          <Button variant="ghost" size="icon" data-testid={`view-invoice-${invoice.id}`}>
                            <Eye className="w-4 h-4" />
                          </Button>
                        </Link>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            {/* Pagination */}
            {Math.ceil(invoices.length / ITEMS_PER_PAGE) > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t">
                <p className="text-sm text-muted-foreground">
                  Showing {((currentPage - 1) * ITEMS_PER_PAGE) + 1} to {Math.min(currentPage * ITEMS_PER_PAGE, invoices.length)} of {invoices.length}
                </p>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} data-testid="invoices-prev-page">
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-sm px-2">Page {currentPage} of {Math.ceil(invoices.length / ITEMS_PER_PAGE)}</span>
                  <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.min(Math.ceil(invoices.length / ITEMS_PER_PAGE), p + 1))} disabled={currentPage === Math.ceil(invoices.length / ITEMS_PER_PAGE)} data-testid="invoices-next-page">
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
            </>
          ) : (
            <div className="empty-state py-12">
              <FileText className="w-16 h-16 mx-auto text-muted-foreground/50" />
              <p className="mt-4 text-lg">No invoices found</p>
              <Link to="/invoices/new">
                <Button className="mt-4">Create First Invoice</Button>
              </Link>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
